import { ContentEdit } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getMostRecentFieldValue } from '@fs/zion-sls'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

/**
 * DanglingFieldButton component renders a ListItem that represents a field with a dangling state.
 * It displays the field's label, and its most recent value. It also includes an edit icon that
 * triggers the onClick function when clicked.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.field - The field object containing information about the form field.
 * @param {function} props.onClick - Function to handle the click event of the ListItem.
 *
 * @returns {JSX.Element} The rendered component
 */
const DanglingFieldButton = ({ field, onClick }) => {
  const [t] = useTranslation()
  const state = useR2StateContext()
  const dispatch = useR2DispatchContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { openOverlay } = useOverlayContext()

  // RII specific functionality
  const handleClick = useCallback(() => {
    const imageIdx = thumbnails.findIndex((tn) => tn.ark === field.partitionKey)

    sharedBrowserDispatch({ type: 'SET_FOCUSED_INDEX', value: imageIdx })
    dispatch({
      type: 'BATCH_DISPATCHES',
      value: [
        { type: 'SET_ACTIVE_FIELD_ID', value: field.id },
        { type: 'SET_PREVIOUS_FOCUSED_INDEX', value: state.focusedIndex },
        { type: 'SET_STATE', value: { dontResetState: true } },
      ],
    })

    openOverlay('editDanglingField')
  }, [dispatch, sharedBrowserDispatch, field.id, field.partitionKey, openOverlay, state.focusedIndex, thumbnails])

  return (
    <ListItem
      data-testid={field?.fieldType || field?.elementType}
      overline={field.displayLabel}
      primaryText={getMostRecentFieldValue(field) || ''}
      secondaryText={t(
        'edit-event.shared-house-number.caption',
        'Information is highlighted on a different page. Edit in context.'
      )}
      endElement={<ListItem.Icon Icon={ContentEdit} />}
      onClick={onClick || handleClick}
    />
  )
}

export default DanglingFieldButton
